/**
 * ADR-0242 — session cache for fetched operator logos.
 *
 * One manifest request per 10 minutes (shared by every row on screen); one
 * image request per agent per logo version, kept as an object URL for the
 * life of the page. Rows for agents without a fetched logo make no image
 * request at all. Any failure resolves to `null` (the caller falls back to
 * a monogram), never throws into render.
 *
 * Object URLs (review N4): one per agent per logo version, shared by every
 * row showing that agent, so they are not revoked when one row unmounts
 * (another row may still draw it). A URL is revoked when a new version of
 * that agent's logo replaces it, and all of them on reset.
 */

import { useEffect, useState } from 'react';

import { fetchAgentLogoBlob, fetchAgentLogoManifest, type AgentLogoManifest } from '../../api/agent-logos.js';
import { useOptionalHttp } from '../../api/http-context.js';
import type { HttpClient } from '../../api/http-client.js';

export const MANIFEST_TTL_MS = 10 * 60 * 1000;

let manifest: { at: number; p: Promise<AgentLogoManifest> } | null = null;
const urlPromises = new Map<string, Promise<string | null>>();
const resolvedUrls = new Map<string, string | null>();
/** agent_id → the version key whose object URL is current. */
const currentKey = new Map<string, string>();

function revoke(url: string | null): void {
  if (url !== null && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(url);
}

function getManifest(http: HttpClient, now: number): Promise<AgentLogoManifest> {
  if (manifest !== null && now - manifest.at < MANIFEST_TTL_MS) return manifest.p;
  const p = fetchAgentLogoManifest(http).catch(() => new Map<string, string>() as AgentLogoManifest);
  manifest = { at: now, p };
  return p;
}

/** Sync read of an already-resolved logo URL (avoids a flash on remount). */
export function cachedAgentLogoUrl(agent_id: string): string | null | undefined {
  return resolvedUrls.get(agent_id);
}

export async function loadAgentLogoUrl(http: HttpClient, agent_id: string): Promise<string | null> {
  const m = await getManifest(http, Date.now());
  const v = m.get(agent_id);
  if (v === undefined) {
    resolvedUrls.set(agent_id, null);
    return null;
  }
  const key = `${agent_id}@${v}`;
  let p = urlPromises.get(key);
  if (p === undefined) {
    p = fetchAgentLogoBlob(http, agent_id, v)
      .then((blob) => (blob !== null && typeof URL.createObjectURL === 'function' ? URL.createObjectURL(blob) : null))
      .catch(() => null);
    urlPromises.set(key, p);
  }
  const url = await p;
  const prevKey = currentKey.get(agent_id);
  if (prevKey !== undefined && prevKey !== key) {
    // A newer logo version replaced the old one: free the old blob.
    const old = urlPromises.get(prevKey);
    urlPromises.delete(prevKey);
    if (old !== undefined) void old.then(revoke);
  }
  currentKey.set(agent_id, key);
  resolvedUrls.set(agent_id, url);
  return url;
}

/**
 * The fetched logo's object URL for `agent_id`, or null (none, loading, or
 * no API in this tree — test scaffolds without an `HttpProvider`).
 */
export function useFetchedAgentLogo(agent_id: string | null | undefined): string | null {
  const http = useOptionalHttp();
  const [url, setUrl] = useState<string | null>(() =>
    agent_id !== null && agent_id !== undefined ? cachedAgentLogoUrl(agent_id) ?? null : null,
  );
  useEffect(() => {
    if (http === null || agent_id === null || agent_id === undefined) {
      setUrl(null);
      return undefined;
    }
    let live = true;
    const cached = cachedAgentLogoUrl(agent_id);
    if (cached !== undefined) setUrl(cached);
    void loadAgentLogoUrl(http, agent_id).then((u) => {
      if (live) setUrl(u);
    });
    return () => {
      live = false;
    };
  }, [http, agent_id]);
  return url;
}

/** Drop the cache and revoke every object URL it holds. */
export function resetAgentLogoStore(): void {
  for (const p of urlPromises.values()) void p.then(revoke);
  manifest = null;
  urlPromises.clear();
  currentKey.clear();
  resolvedUrls.clear();
}

export function __resetAgentLogoStoreForTests(): void {
  manifest = null;
  currentKey.clear();
  urlPromises.clear();
  resolvedUrls.clear();
}
